import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { fetchCopyMap, upsertCopy, type CopyMap } from './copy';

const defaultCopy: CopyMap = {
  'home.hero.title': 'Sponsor a Classroom, Empower a Future',
  'home.hero.tagline': 'Project Tower connects committed sponsors with passionate classrooms so every student has the tools to dream bigger.',
  'home.cta.sponsor': 'Sponsor Portal',
  'home.cta.teacher': 'Teacher Portal',
  'home.how.title': 'How it works',
  'home.how.tagline': 'Sponsor → Classroom → Impact',
  'home.steps.sponsor.title': 'Sponsor',
  'home.steps.sponsor.body': 'Choose a classroom story that resonates and make a pledge in minutes.',
  'home.steps.classroom.title': 'Classroom',
  'home.steps.classroom.body': 'Teachers receive funds, plan experiences, and keep everyone in the loop.',
  'home.steps.impact.title': 'Impact',
  'home.steps.impact.body': 'See progress, photos, and milestones that celebrate student growth.',
  'home.testimonials.title': 'Voices from our community',
  'home.testimonials.jordan.quote': '“Our sponsorship helped Ms. Lee bring robotics to her 5th graders. Seeing their excitement reminded us why we give.”',
  'home.testimonials.jordan.name': 'Jordan, Community Sponsor',
  'home.testimonials.rivera.quote': '“Project Tower made it effortless to request support and keep my donors updated with real classroom wins.”',
  'home.testimonials.rivera.name': 'Ms. Rivera, 3rd Grade Teacher',
  'home.testimonials.avery.quote': '“The transparency and storytelling keep our team connected to the students we’re investing in.”',
  'home.testimonials.avery.name': 'Avery, Corporate Partner',
  'mission.hero.kicker': 'Our Mission',
  'mission.hero.title': 'Every student deserves a launchpad.',
  'mission.hero.description': 'We partner with teachers, sponsors, and communities to remove friction between imagination and opportunity. By stewarding resources and storytelling with heart, Project Tower elevates classrooms so ideas can reach new heights.',
  'mission.sections.problem.title': 'The Problem',
  'mission.sections.problem.intro': 'Classrooms need flexible funding to keep pace with what their students are dreaming up, yet educators often shoulder the financial gap themselves.',
  'mission.sections.problem.bullets.0': 'Traditional budgets rarely stretch to cover enrichment, emerging technology, or the supplies that make ideas tangible.',
  'mission.sections.problem.bullets.1': 'Fundraising takes precious time away from teaching, and piecemeal efforts make it hard for communities to stay engaged.',
  'mission.sections.problem.bullets.2': 'Sponsors want visibility into impact but rarely see beyond a single thank-you note or spreadsheet update.',
  'mission.sections.approach.title': 'Our Approach',
  'mission.sections.approach.intro': 'Project Tower pairs direct classroom sponsorship with a steady drumbeat of storytelling so every partner can see the difference their support makes.',
  'mission.sections.approach.bullets.0': 'Teachers publish programs with specific goals, milestones, and materials their students need most.',
  'mission.sections.approach.bullets.1': 'Sponsors back classrooms instantly and receive guided updates, photos, and receipts along the way.',
  'mission.sections.approach.bullets.2': 'Our team handles payouts, compliance, and gratitude touches so relationships stay strong and focused on learning.',
  'mission.sections.success.title': 'What Success Looks Like',
  'mission.sections.success.intro': 'We celebrate progress with concrete outcomes that signal classrooms are resourced, students are thriving, and partners return year after year.',
  'mission.sections.success.bullets.0': 'Learning experiences are launched without delay, with real-time updates that highlight student voices.',
  'mission.sections.success.bullets.1': 'Teachers reclaim hours each month to focus on instruction instead of fundraising logistics.',
  'mission.sections.success.bullets.2': 'Sponsors see transparent impact metrics that inspire renewed commitments and introductions to new partners.',
  'mission.principles.title': 'Our Principles',
  'mission.principles.description': 'These commitments ground every partnership we build and every classroom story we champion.',
  'mission.principles.items.0': 'Lead with transparency and timely communication.',
  'mission.principles.items.1': 'Center teacher voice in every decision and deliverable.',
  'mission.principles.items.2': 'Design for equity so resources reach classrooms with the greatest needs.',
  'mission.principles.items.3': 'Measure outcomes that reflect both academic and social-emotional growth.',
  'mission.principles.items.4': 'Honor sponsor intent with clear budgeting and stewardship.',
  'mission.principles.items.5': 'Celebrate wins publicly to inspire the next wave of supporters.',
  'mission.quote.body': '“When classrooms have what they need, students walk in with confidence and leave ready to change the world.”',
  'mission.quote.attribution': '— Project Tower Partner Teacher',
  'about.hero.kicker': 'About',
  'about.hero.title': 'About Project Tower',
  'about.hero.tagline': 'We elevate classrooms by pairing imaginative educators with the sponsors and partners who can help them soar. Through thoughtful stewardship, transparent reporting, and joyful storytelling, we remove the barriers between bold ideas and the resources they need.',
  'about.story.title': 'Our Story',
  'about.story.body.0': 'Project Tower was born from the belief that every student deserves the chance to dream audaciously. We started as a volunteer collective, helping teachers source supplies and experiences for their classrooms. Today, we connect classrooms across the region with sponsors who invest in hands-on learning, mentorship, and community experiences.',
  'about.story.body.1': 'By combining technology with human relationships, we make it easy for supporters to see their impact and for educators to focus on what matters most—sparking curiosity and confidence in their students.',
  'about.values.transparency.title': 'Transparency',
  'about.values.transparency.description': 'We keep sponsors, teachers, and families informed every step of the journey with real-time updates and open reporting.',
  'about.values.equity.title': 'Equity',
  'about.values.equity.description': 'We center classrooms that are historically under-resourced so every student can access joyful, enriching learning.',
  'about.values.partnership.title': 'Partnership',
  'about.values.partnership.description': 'We build long-term relationships between educators, community partners, and donors to amplify collective impact.',
  'about.stats.classes.value': '128',
  'about.stats.classes.label': 'Classes Sponsored',
  'about.stats.sponsors.value': '342',
  'about.stats.sponsors.label': 'Total Sponsors',
  'about.stats.funding.value': '$485K',
  'about.stats.funding.label': 'Total Funding',
  'about.team.title': 'Meet the Team',
  'about.team.subtitle': 'A dedicated group of educators, technologists, and community builders uplifting classrooms together.',
  'about.team.members.jordan.name': 'Jordan Ellis',
  'about.team.members.jordan.role': 'Executive Director',
  'about.team.members.priya.name': 'Priya Patel',
  'about.team.members.priya.role': 'Programs Lead',
  'about.team.members.marcus.name': 'Marcus Lee',
  'about.team.members.marcus.role': 'Community Partnerships',
  'about.team.members.sofia.name': 'Sofia Ramirez',
  'about.team.members.sofia.role': 'Operations & Finance',
  'contact.hero.kicker': 'Get in touch',
  'contact.hero.title': 'We’re here for classrooms, sponsors, and community partners.',
  'contact.hero.body': 'Have a question about partnering with Project Tower or supporting a classroom? Drop us a note and our team will reach out within one business day.',
  'contact.form.title': 'Send us a message',
  'contact.form.subtitle': 'Fill out the form and someone from the Project Tower team will reply shortly with the information you need.',
  'contact.form.submit': 'Send message',
  'contact.card.title': 'Visit Project Tower',
  'contact.card.address': '123 Lighthouse Ave.\nSan Francisco, CA 94107',
  'contact.card.emailLabel': 'Email',
  'contact.card.emailValue': 'hello@projecttower.org',
  'contact.card.phoneLabel': 'Phone',
  'contact.card.phoneValue': '(415) 555-1234',
  'contact.card.hoursLabel': 'Office hours',
  'contact.card.hoursValue': 'Monday–Friday, 9am–5pm PT',
  'contact.thanks.title': 'Thank you for reaching out!',
  'contact.thanks.body': 'We received your message and will respond soon. In the meantime, feel free to explore programs making an impact right now.',
  'contact.thanks.homeCta': 'Back to Home',
  'contact.thanks.programsCta': 'View Programs',
  'contact.thanks.footerPrefix': 'Need to update your message? Email us directly at',
  'contact.thanks.footerEmail': 'hello@projecttower.org',
  'contact.thanks.footerSuffix': '.',
  'admin.copy.title': 'Site Copy',
  'admin.copy.subtitle': 'Manage the public-facing copy used across Project Tower pages.',
}

type CopyTranslator = <T extends string | symbol>(key: string, defaultValue: T) => string | T;

type CopyContextValue = {
  t: CopyTranslator;
  ready: boolean;
  map: CopyMap;
  status?: string;
};

const CopyCtx = createContext<CopyContextValue>({
  t: (_key, defaultValue) => defaultValue,
  ready: false,
  map: {},
});

async function loadFallbackCopy(): Promise<CopyMap> {
  try {
    const response = await fetch('/copy.json')
    if (!response.ok) throw new Error('Failed to load copy.json')
    const fallback = await response.json()
    return Object.keys(fallback).length ? fallback : defaultCopy
  } catch (error) {
    return defaultCopy
  }
}

export function CopyProvider({ children }: PropsWithChildren) {
  const [map, setMap] = useState<CopyMap | null>(null);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const remote = await fetchCopyMap();
        if (!mounted) return;
        if (Object.keys(remote).length === 0) {
          setMap(defaultCopy);
          setStatus('seeded');
          try {
            await upsertCopy(
              Object.entries(defaultCopy).map(([key, value]) => ({ key, value })),
            );
          } catch (error) {
            // ignore RLS failures for non-admin users
          }
        } else {
          setMap(remote);
          setStatus('ready');
        }
      } catch (error) {
        if (!mounted) return;
        const fallback = await loadFallbackCopy();
        if (!mounted) return;
        setMap(fallback);
        setStatus('fallback');
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const value = useMemo((): CopyContextValue => {
    const ready = map !== null;
    const t: CopyTranslator = (key, defaultValue) =>
      map && key in map ? map[key] : defaultValue;
    return { t, ready, map: map || {}, status };
  }, [map, status]);

  return <CopyCtx.Provider value={value}>{children}</CopyCtx.Provider>;
}

export function useCopy() {
  return useContext(CopyCtx);
}

const SENTINEL = Symbol('copy-fallback');

type CopyProps = PropsWithChildren<{
  k: string;
}>;

export function Copy({ k, children }: CopyProps) {
  const { t } = useCopy();
  const value = t(k, SENTINEL);
  if (value === SENTINEL) {
    return children ?? '';
  }
  return value;
}
